import React, { useCallback } from 'react'
import { Button, Modal } from 'react-bootstrap'
import { PermissionForm, PermissionSchema } from '../schema/PermissionSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCreatePermission } from '../hooks/useCreatePermission';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

interface CreatePermissionDialogProps {
    show: boolean;
    onClose: () => void;
}

const CreatePermissionDialog: React.FC<CreatePermissionDialogProps> = ({
    show, onClose
}) => {
    const { register, handleSubmit, formState: { errors }, reset, setError } = useForm<PermissionForm>({
        resolver: zodResolver(PermissionSchema),
    });

    const handleClose = () => {
        reset();
        onClose();
    }

    const { mutate, isPending } = useCreatePermission();

    const queryClient = useQueryClient();

    const onSubmit = useCallback((values: PermissionForm) => {
        toast.loading('Creating permission...', {
            id: 'create-permission',
        });
        mutate(values, {
            onSuccess: async () => {
                handleClose();

                await queryClient.refetchQueries({
                    predicate: (query) => query.queryKey.some((key) => ['permissions'].includes(key as string))
                });

                toast.success('Permission created successfully', {
                    id: 'create-permission',
                });
            },
            onError: (mutationError) => {
                if ((mutationError as any)?.status === 422) {
                    const validationErrors = (mutationError as any)?.response?.data?.errors;
                    if (validationErrors) {
                        for (const [field, messages] of Object.entries(validationErrors)) {
                            setError(field as keyof PermissionForm, {
                                type: "manual",
                                message: (messages as string[])[0],
                            });
                        }
                        toast.error('Failed to create permission', { id: 'create-permission' });
                    }
                } else {
                    toast.error(mutationError?.message || 'Failed to create permission', { id: 'create-permission' });
                }
            }
        });
    }, [mutate]);

    return (
        <Modal
            show={show}
            onHide={handleClose}
            backdrop="static"
            keyboard={false}
            centered
        >
            <Modal.Header closeButton>
                <Modal.Title>Create Permission</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <form onSubmit={handleSubmit(onSubmit)} className="d-flex flex-column">
                    <div className="mb-3">
                        <label htmlFor="name" className="form-label">Name</label>
                        <input
                            type="text"
                            className={`form-control ${errors.name && 'is-invalid'}`}
                            id="name"
                            placeholder='Enter permission name'
                            {...register('name')}
                        />
                        {errors.name && <div className="invalid-feedback">{errors.name.message}</div>}
                    </div>
                    <div className='mb-3'>
                        <label htmlFor='group' className='form-label'>Group</label>
                        <input
                            type='text'
                            className={`form-control ${errors.group && 'is-invalid'}`}
                            id='group'
                            placeholder='Enter permission group'
                            {...register('group')}
                        />
                        {errors.group && <div className='invalid-feedback'>{errors.group.message}</div>}
                    </div>
                </form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Close
                </Button>
                <Button variant="primary" type='submit' onClick={handleSubmit(onSubmit)} disabled={isPending}>
                    {isPending ? 'Saving...' : 'Save'}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

export default CreatePermissionDialog